import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  FileText, Plus, Euro, Clock, AlertTriangle, Check, X,
  Building2, Calendar, Shield, CreditCard, Users, Tractor,
  TrendingUp, Lock, Unlock, ChevronRight, Download, Eye,
  Scale, Percent, Timer, FileCheck, AlertCircle, Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== COMPOSANTS ====================

// Badge de statut
const StatutBadge = ({ statut }) => {
  const config = {
    brouillon: { label: 'Brouillon', class: 'bg-gray-100 text-gray-700' },
    en_attente_signature: { label: 'En attente', class: 'bg-amber-100 text-amber-700' },
    actif: { label: 'Actif', class: 'bg-green-100 text-green-700' },
    suspendu: { label: 'Suspendu', class: 'bg-red-100 text-red-700' },
    termine: { label: 'Terminé', class: 'bg-blue-100 text-blue-700' },
    resilie: { label: 'Résilié', class: 'bg-gray-100 text-gray-500' }
  };
  const c = config[statut] || { label: statut, class: 'bg-gray-100' };
  return <Badge className={c.class}>{c.label}</Badge>;
};

// Badge de niveau client (score)
const NiveauBadge = ({ niveau }) => {
  const config = {
    partenaire: { label: 'Partenaire', class: 'bg-green-100 text-green-700', icon: Star },
    standard: { label: 'Standard', class: 'bg-blue-100 text-blue-700', icon: Check },
    risque: { label: 'À risque', class: 'bg-red-100 text-red-700', icon: AlertTriangle }
  };
  const c = config[niveau] || config.standard;
  const Icon = c.icon;
  return (
    <Badge className={c.class}>
      <Icon className="w-3 h-3 mr-1" />
      {c.label}
    </Badge>
  );
};

// Carte de contrat
const ContratCard = ({ contrat, onVoir }) => (
  <Card 
    className={`cursor-pointer hover:shadow-lg transition-all ${contrat.bloque ? 'border-red-300 bg-red-50/50' : ''}`}
    onClick={() => onVoir(contrat)}
    data-testid={`contrat-card-${contrat.id}`}
  >
    <CardContent className="p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Badge variant="outline" className="text-xs">{contrat.numero}</Badge>
            <StatutBadge statut={contrat.statut} />
            {contrat.bloque && (
              <Badge className="bg-red-500 text-white">
                <Lock className="w-3 h-3 mr-1" /> Bloqué
              </Badge>
            )}
          </div>
          <h3 className="font-semibold text-lg">{contrat.client_nom}</h3>
          <p className="text-sm text-muted-foreground">
            {contrat.type_contrat} • {contrat.date_debut} → {contrat.date_fin}
          </p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-primary">
            {contrat.montant_total_prevu?.toLocaleString('fr-FR')} €
          </p>
          <p className="text-xs text-muted-foreground">HT prévu</p>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-muted-foreground">
          <span>Réalisé: {contrat.montant_realise?.toLocaleString('fr-FR')} €</span>
          <span>{Math.round((contrat.montant_realise / contrat.montant_total_prevu) * 100 || 0)}%</span>
        </div>
        <Progress value={(contrat.montant_realise / contrat.montant_total_prevu) * 100 || 0} className="h-2" />
      </div>

      {/* Infos complémentaires */}
      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <FileText className="w-3 h-3" />
          {contrat.prestations?.length || 0} prestations
        </span>
        <span className="flex items-center gap-1">
          <Euro className="w-3 h-3" />
          Facturé: {contrat.montant_facture?.toLocaleString('fr-FR')} €
        </span>
        {contrat.total_acomptes > 0 && (
          <span className="flex items-center gap-1 text-green-600">
            <CreditCard className="w-3 h-3" />
            Acompte: {contrat.total_acomptes?.toLocaleString('fr-FR')} €
          </span>
        )}
      </div>
    </CardContent>
  </Card>
);

// Modal création de contrat
const NouveauContratModal = ({ open, onClose, clients, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [typesPrestations, setTypesPrestations] = useState([]);
  const [scoreClient, setScoreClient] = useState(null);
  
  const [form, setForm] = useState({
    // Client
    client_id: '',
    client_nom: '',
    client_siret: '',
    client_adresse: '',
    
    // Type
    type_contrat: 'campagne_annuelle',
    duree_type: 'campagne',
    date_debut: new Date().toISOString().split('T')[0],
    date_fin: `${new Date().getFullYear()}-12-31`,
    annee_campagne: new Date().getFullYear(),
    tacite_reconduction: true,
    
    // Prestations
    prestations: [],
    
    // Conditions financières
    mode_facturation: 'mensuel',
    delai_paiement: 30,
    acompte_obligatoire: false,
    acompte_pourcentage: 20,
    suspension_auto_jours: 15,
    frais_remobilisation: 350,
    clause_exclusivite: false,
    
    // Matériel/Personnel
    materiel_fourni_par: 'eta',
    personnel_type: 'eta',
    
    // Options juridiques
    clause_meteo: true,
    clause_force_majeure: true,
    
    notes: ''
  });

  useEffect(() => {
    if (open) {
      loadTypesPrestations();
    }
  }, [open]);

  useEffect(() => {
    if (form.client_id) {
      loadScoreClient(form.client_id);
    }
  }, [form.client_id]);

  const loadTypesPrestations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-eta/types-prestations`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTypesPrestations(data.prestations || []);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const loadScoreClient = async (clientId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-eta/client/${clientId}/score`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setScoreClient(await res.json());
      }
    } catch (err) {
      console.error(err);
    }
  };

  const selectClient = (client) => {
    setForm({
      ...form,
      client_id: client.id,
      client_nom: client.nom || client.entreprise,
      client_siret: client.siret || '',
      client_adresse: client.adresse || ''
    });
  };

  const ajouterPrestation = (presta) => {
    const existe = form.prestations.find(p => p.code === presta.code);
    if (!existe) {
      setForm({
        ...form,
        prestations: [...form.prestations, {
          code: presta.code,
          libelle: presta.libelle,
          quantite_prevue: 10, // Défaut
          unite: presta.unite,
          tarif_unitaire: presta.tarif_defaut
        }]
      });
    }
  };

  const updatePrestation = (index, field, value) => {
    const updated = [...form.prestations];
    updated[index] = { ...updated[index], [field]: value };
    setForm({ ...form, prestations: updated });
  };

  const supprimerPrestation = (index) => {
    setForm({
      ...form,
      prestations: form.prestations.filter((_, i) => i !== index)
    });
  };

  const calculerTotal = () => {
    return form.prestations.reduce((sum, p) => sum + (p.quantite_prevue * p.tarif_unitaire), 0);
  };

  const handleSubmit = async () => {
    if (!form.client_id || form.prestations.length === 0) {
      toast.error('Sélectionnez un client et ajoutez des prestations');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-eta`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...form,
          conditions_financieres: {
            mode_facturation: form.mode_facturation,
            delai_paiement: form.delai_paiement,
            acompte_obligatoire: form.acompte_obligatoire,
            acompte_pourcentage: form.acompte_pourcentage,
            suspension_auto_jours: form.suspension_auto_jours,
            frais_remobilisation: form.frais_remobilisation,
            clause_exclusivite: form.clause_exclusivite,
            interets_retard_auto: true,
            indemnite_40_euros: true
          },
          materiel_personnel: {
            materiel_fourni_par: form.materiel_fourni_par,
            personnel_type: form.personnel_type
          }
        })
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(`Contrat ${data.numero} créé avec succès`);
        onSuccess?.();
        onClose();
        resetForm();
      } else {
        throw new Error('Erreur création');
      }
    } catch (err) {
      toast.error('Erreur lors de la création du contrat');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      client_id: '', client_nom: '', client_siret: '', client_adresse: '',
      type_contrat: 'campagne_annuelle', duree_type: 'campagne',
      date_debut: new Date().toISOString().split('T')[0],
      date_fin: `${new Date().getFullYear()}-12-31`,
      annee_campagne: new Date().getFullYear(),
      tacite_reconduction: true, prestations: [],
      mode_facturation: 'mensuel', delai_paiement: 30,
      acompte_obligatoire: false, acompte_pourcentage: 20,
      suspension_auto_jours: 15, frais_remobilisation: 350,
      clause_exclusivite: false, materiel_fourni_par: 'eta',
      personnel_type: 'eta', clause_meteo: true, clause_force_majeure: true, notes: ''
    });
    setStep(1);
    setScoreClient(null);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5" />
            Nouveau Contrat ETA
          </DialogTitle>
          <DialogDescription>
            Étape {step}/4 - {['Client', 'Prestations', 'Conditions', 'Validation'][step-1]}
          </DialogDescription>
        </DialogHeader>

        {/* Indicateur étapes */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3, 4].map(s => (
            <div
              key={s}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                step >= s ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}
            >
              {step > s ? <Check className="w-5 h-5" /> : s}
            </div>
          ))}
        </div>

        {/* Étape 1: Client */}
        {step === 1 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Sélectionner le client</h3>
            
            <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto">
              {clients.map(client => (
                <button
                  key={client.id}
                  onClick={() => selectClient(client)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    form.client_id === client.id 
                      ? 'border-primary bg-primary/10' 
                      : 'border-muted hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold">{client.nom || client.entreprise}</p>
                      <p className="text-xs text-muted-foreground">{client.commune}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Score client */}
            {scoreClient && (
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">Score client</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-3xl font-bold">{scoreClient.score_total}</span>
                        <span className="text-sm text-muted-foreground">/100</span>
                        <NiveauBadge niveau={scoreClient.niveau} />
                      </div>
                    </div>
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">Recommandations:</p>
                      <p>Acompte: <strong>{scoreClient.recommandations.acompte_recommande}%</strong></p>
                      <p>Paiement: <strong>{scoreClient.recommandations.mode_paiement}</strong></p>
                    </div>
                  </div>
                  {scoreClient.alertes?.length > 0 && (
                    <div className="mt-2 pt-2 border-t">
                      {scoreClient.alertes.map((a, i) => (
                        <p key={i} className="text-sm text-amber-600 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" /> {a}
                        </p>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Type de contrat */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type de contrat</Label>
                <Select value={form.type_contrat} onValueChange={v => setForm({...form, type_contrat: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="campagne_annuelle">Campagne annuelle</SelectItem>
                    <SelectItem value="pluriannuel">Pluriannuel</SelectItem>
                    <SelectItem value="ponctuel">Intervention ponctuelle</SelectItem>
                    <SelectItem value="mise_dispo_materiel">Mise à dispo matériel</SelectItem>
                    <SelectItem value="mise_dispo_personnel">Mise à dispo personnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Année / Campagne</Label>
                <Input 
                  type="number"
                  value={form.annee_campagne}
                  onChange={e => setForm({...form, annee_campagne: parseInt(e.target.value)})}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Date début</Label>
                <Input 
                  type="date"
                  value={form.date_debut}
                  onChange={e => setForm({...form, date_debut: e.target.value})}
                />
              </div>
              <div>
                <Label>Date fin</Label>
                <Input 
                  type="date"
                  value={form.date_fin}
                  onChange={e => setForm({...form, date_fin: e.target.value})}
                />
              </div>
            </div>
          </div>
        )}

        {/* Étape 2: Prestations */}
        {step === 2 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Prestations incluses</h3>

            {/* Liste prestations ajoutées */}
            {form.prestations.length > 0 && (
              <div className="space-y-2 mb-4">
                {form.prestations.map((p, idx) => (
                  <Card key={idx} className="bg-primary/5">
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="font-medium">{p.libelle}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={p.quantite_prevue}
                                onChange={e => updatePrestation(idx, 'quantite_prevue', parseFloat(e.target.value))}
                                className="w-20 h-8"
                              />
                              <span className="text-sm text-muted-foreground">{p.unite}</span>
                            </div>
                            <span className="text-muted-foreground">×</span>
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={p.tarif_unitaire}
                                onChange={e => updatePrestation(idx, 'tarif_unitaire', parseFloat(e.target.value))}
                                className="w-20 h-8"
                              />
                              <span className="text-sm text-muted-foreground">€/{p.unite}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold">
                            {(p.quantite_prevue * p.tarif_unitaire).toLocaleString('fr-FR')} €
                          </p>
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500"
                            onClick={() => supprimerPrestation(idx)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <div className="flex justify-end p-2 bg-primary/10 rounded-lg">
                  <span className="font-bold text-lg">
                    Total: {calculerTotal().toLocaleString('fr-FR')} € HT
                  </span>
                </div>
              </div>
            )}

            {/* Catalogue prestations */}
            <div className="border rounded-lg p-3 max-h-60 overflow-y-auto">
              <p className="text-sm text-muted-foreground mb-2">Cliquez pour ajouter:</p>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {typesPrestations.map(p => (
                  <button
                    key={p.code}
                    onClick={() => ajouterPrestation(p)}
                    disabled={form.prestations.some(fp => fp.code === p.code)}
                    className={`p-2 text-left rounded-lg border text-sm transition-all ${
                      form.prestations.some(fp => fp.code === p.code)
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'hover:border-primary hover:bg-primary/5'
                    }`}
                  >
                    <p className="font-medium truncate">{p.libelle}</p>
                    <p className="text-xs text-muted-foreground">{p.tarif_defaut}€/{p.unite}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Étape 3: Conditions financières */}
        {step === 3 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Conditions financières & sécurisation</h3>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Facturation */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Euro className="w-4 h-4" /> Facturation
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Mode de facturation</Label>
                    <Select value={form.mode_facturation} onValueChange={v => setForm({...form, mode_facturation: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mensuel">Mensuel</SelectItem>
                        <SelectItem value="avancement">À l'avancement</SelectItem>
                        <SelectItem value="intervention">Par intervention</SelectItem>
                        <SelectItem value="forfait_campagne">Forfait campagne</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Délai de paiement (jours)</Label>
                    <Select value={String(form.delai_paiement)} onValueChange={v => setForm({...form, delai_paiement: parseInt(v)})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Comptant</SelectItem>
                        <SelectItem value="30">30 jours</SelectItem>
                        <SelectItem value="45">45 jours</SelectItem>
                        <SelectItem value="60">60 jours</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Acompte */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <CreditCard className="w-4 h-4" /> Acompte
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Acompte obligatoire</Label>
                    <Checkbox 
                      checked={form.acompte_obligatoire}
                      onCheckedChange={c => setForm({...form, acompte_obligatoire: c})}
                    />
                  </div>
                  {form.acompte_obligatoire && (
                    <div>
                      <Label>Pourcentage d'acompte</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          value={form.acompte_pourcentage}
                          onChange={e => setForm({...form, acompte_pourcentage: parseFloat(e.target.value)})}
                          className="w-20"
                        />
                        <span>%</span>
                        <span className="text-sm text-muted-foreground">
                          = {(calculerTotal() * form.acompte_pourcentage / 100).toLocaleString('fr-FR')} € HT
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sécurisation */}
            <Card className="bg-amber-50/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Sécurisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Suspension automatique après (jours de retard)</Label>
                    <Input
                      type="number"
                      value={form.suspension_auto_jours}
                      onChange={e => setForm({...form, suspension_auto_jours: parseInt(e.target.value)})}
                    />
                  </div>
                  <div>
                    <Label>Frais de remobilisation (€ HT)</Label>
                    <Input
                      type="number"
                      value={form.frais_remobilisation}
                      onChange={e => setForm({...form, frais_remobilisation: parseFloat(e.target.value)})}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Clause d'exclusivité partielle</Label>
                  <Checkbox 
                    checked={form.clause_exclusivite}
                    onCheckedChange={c => setForm({...form, clause_exclusivite: c})}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Matériel et personnel */}
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Matériel fourni par</Label>
                <Select value={form.materiel_fourni_par} onValueChange={v => setForm({...form, materiel_fourni_par: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eta">ETA Sauvayre</SelectItem>
                    <SelectItem value="client">Le client</SelectItem>
                    <SelectItem value="mixte">Mixte</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Personnel</Label>
                <Select value={form.personnel_type} onValueChange={v => setForm({...form, personnel_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="eta">Personnel ETA</SelectItem>
                    <SelectItem value="dedie_client">Personnel dédié</SelectItem>
                    <SelectItem value="chauffeur_seul">Chauffeur seul</SelectItem>
                    <SelectItem value="equipe_complete">Équipe complète</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {/* Étape 4: Validation */}
        {step === 4 && (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Récapitulatif du contrat</h3>

            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Client */}
                <div className="flex items-center gap-3 pb-3 border-b">
                  <Building2 className="w-10 h-10 text-primary" />
                  <div>
                    <p className="font-bold text-lg">{form.client_nom}</p>
                    <p className="text-sm text-muted-foreground">
                      {form.type_contrat} • {form.date_debut} → {form.date_fin}
                    </p>
                  </div>
                </div>

                {/* Prestations */}
                <div>
                  <p className="font-medium mb-2">Prestations ({form.prestations.length})</p>
                  <div className="space-y-1 text-sm">
                    {form.prestations.map((p, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{p.libelle}</span>
                        <span className="font-medium">
                          {p.quantite_prevue} {p.unite} × {p.tarif_unitaire}€ = {(p.quantite_prevue * p.tarif_unitaire).toLocaleString('fr-FR')}€
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Total */}
                <div className="bg-primary/10 p-3 rounded-lg">
                  <div className="flex justify-between text-lg font-bold">
                    <span>MONTANT TOTAL PRÉVU</span>
                    <span>{calculerTotal().toLocaleString('fr-FR')} € HT</span>
                  </div>
                  <div className="flex justify-between text-sm text-muted-foreground">
                    <span>TVA 20%</span>
                    <span>{(calculerTotal() * 0.2).toLocaleString('fr-FR')} €</span>
                  </div>
                  <div className="flex justify-between font-bold">
                    <span>TTC</span>
                    <span>{(calculerTotal() * 1.2).toLocaleString('fr-FR')} €</span>
                  </div>
                </div>

                {/* Conditions */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Facturation</p>
                    <p className="font-medium">{form.mode_facturation}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Paiement</p>
                    <p className="font-medium">{form.delai_paiement} jours</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Acompte</p>
                    <p className="font-medium">
                      {form.acompte_obligatoire ? `${form.acompte_pourcentage}%` : 'Non'}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Suspension auto</p>
                    <p className="font-medium">{form.suspension_auto_jours} jours</p>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes (optionnel)</Label>
                  <Textarea
                    value={form.notes}
                    onChange={e => setForm({...form, notes: e.target.value})}
                    placeholder="Conditions particulières, remarques..."
                    rows={2}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Navigation */}
        <div className="flex justify-between pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => step > 1 ? setStep(step - 1) : onClose()}
          >
            {step > 1 ? 'Retour' : 'Annuler'}
          </Button>

          {step < 4 ? (
            <Button
              onClick={() => setStep(step + 1)}
              disabled={step === 1 && !form.client_id || step === 2 && form.prestations.length === 0}
            >
              Suivant <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={loading}
              className="bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Création...' : 'Créer le contrat'}
              <FileCheck className="w-4 h-4 ml-1" />
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ==================== PAGE PRINCIPALE ====================

const ContratsETAPage = () => {
  const [contrats, setContrats] = useState([]);
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewContrat, setShowNewContrat] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [annee, setAnnee] = useState(new Date().getFullYear());

  useEffect(() => {
    loadData();
  }, [annee, filtreStatut]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Charger contrats
      let url = `${API_URL}/api/contrats-eta?annee=${annee}`;
      if (filtreStatut !== 'tous') url += `&statut=${filtreStatut}`;
      
      const [contratsRes, statsRes, clientsRes] = await Promise.all([
        fetch(url, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/contrats-eta/stats/dashboard?annee=${annee}`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${API_URL}/api/crm/clients`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);

      if (contratsRes.ok) {
        const data = await contratsRes.json();
        setContrats(data.contrats || []);
      }
      
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }

      if (clientsRes.ok) {
        const data = await clientsRes.json();
        setClients(data.clients || data || []);
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const voirContrat = async (contrat) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/contrats-eta/${contrat.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSelectedContrat(data);
        setShowDetail(true);
      }
    } catch (err) {
      toast.error('Erreur chargement contrat');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="contrats-eta-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Scale className="w-6 h-6 text-white" />
            </div>
            Contrats & Engagements
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestion des contrats ETA Sauvayre
          </p>
        </div>
        
        <Button 
          size="lg"
          className="gap-2 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
          onClick={() => setShowNewContrat(true)}
          data-testid="new-contrat-btn"
        >
          <Plus className="w-5 h-5" />
          Nouveau contrat
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.nb_contrats}</p>
                  <p className="text-xs text-muted-foreground">Contrats {annee}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.montant_total_prevu?.toLocaleString('fr-FR')}€</p>
                  <p className="text-xs text-muted-foreground">CA prévu</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Euro className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.montant_facture?.toLocaleString('fr-FR')}€</p>
                  <p className="text-xs text-muted-foreground">Facturé</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_acomptes?.toLocaleString('fr-FR')}€</p>
                  <p className="text-xs text-muted-foreground">Acomptes</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className={stats.clients_bloques > 0 ? 'bg-red-50' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stats.clients_bloques > 0 ? 'bg-red-100' : 'bg-gray-100'
                }`}>
                  <Lock className={`w-5 h-5 ${stats.clients_bloques > 0 ? 'text-red-600' : 'text-gray-600'}`} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.clients_bloques || 0}</p>
                  <p className="text-xs text-muted-foreground">Bloqués</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Label>Année:</Label>
          <Select value={String(annee)} onValueChange={v => setAnnee(parseInt(v))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026, 2027].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center gap-2">
          <Label>Statut:</Label>
          <Select value={filtreStatut} onValueChange={setFiltreStatut}>
            <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tous">Tous</SelectItem>
              <SelectItem value="actif">Actifs</SelectItem>
              <SelectItem value="brouillon">Brouillons</SelectItem>
              <SelectItem value="suspendu">Suspendus</SelectItem>
              <SelectItem value="termine">Terminés</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Liste des contrats */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      ) : contrats.length === 0 ? (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">Aucun contrat</h3>
          <p className="text-muted-foreground mb-4">Créez votre premier contrat ETA</p>
          <Button onClick={() => setShowNewContrat(true)}>
            <Plus className="w-4 h-4 mr-2" /> Nouveau contrat
          </Button>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {contrats.map(c => (
            <ContratCard key={c.id} contrat={c} onVoir={voirContrat} />
          ))}
        </div>
      )}

      {/* Modal nouveau contrat */}
      <NouveauContratModal 
        open={showNewContrat}
        onClose={() => setShowNewContrat(false)}
        clients={clients}
        onSuccess={loadData}
      />

      {/* Modal détail contrat */}
      {selectedContrat && (
        <Dialog open={showDetail} onOpenChange={setShowDetail}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Badge variant="outline">{selectedContrat.contrat?.numero}</Badge>
                {selectedContrat.contrat?.client_nom}
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="detail">
              <TabsList>
                <TabsTrigger value="detail">Détails</TabsTrigger>
                <TabsTrigger value="avancement">Avancement</TabsTrigger>
                <TabsTrigger value="facturation">Facturation</TabsTrigger>
                <TabsTrigger value="document">Document</TabsTrigger>
              </TabsList>

              <TabsContent value="detail" className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Informations</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type:</span>
                        <span>{selectedContrat.contrat?.type_contrat}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Période:</span>
                        <span>{selectedContrat.contrat?.date_debut} → {selectedContrat.contrat?.date_fin}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Statut:</span>
                        <StatutBadge statut={selectedContrat.contrat?.statut} />
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Montants</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Prévu:</span>
                        <span className="font-bold">{selectedContrat.contrat?.montant_total_prevu?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Réalisé:</span>
                        <span>{selectedContrat.contrat?.montant_realise?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Facturé:</span>
                        <span>{selectedContrat.contrat?.montant_facture?.toLocaleString('fr-FR')}€</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Acomptes:</span>
                        <span className="text-green-600">{selectedContrat.contrat?.total_acomptes?.toLocaleString('fr-FR')}€</span>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Score client */}
                {selectedContrat.score_client && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <Star className="w-4 h-4" /> Score client
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center gap-4">
                        <div className="text-4xl font-bold">{selectedContrat.score_client.score_total}</div>
                        <NiveauBadge niveau={selectedContrat.score_client.niveau} />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="avancement" className="space-y-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Avancement global</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4 mb-4">
                      <Progress value={selectedContrat.avancement?.taux_global || 0} className="flex-1" />
                      <span className="font-bold">{selectedContrat.avancement?.taux_global}%</span>
                    </div>
                    
                    <div className="space-y-3">
                      {selectedContrat.avancement?.prestations?.map((p, i) => (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <span>{p.libelle}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">
                              {p.quantite_realisee}/{p.quantite_prevue}
                            </span>
                            <Progress value={p.taux_avancement} className="w-24 h-2" />
                            <span className="w-12 text-right">{p.taux_avancement}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="facturation" className="space-y-4">
                <div className="text-center py-8 text-muted-foreground">
                  <CreditCard className="w-12 h-12 mx-auto mb-2" />
                  <p>Historique de facturation à venir</p>
                </div>
              </TabsContent>

              <TabsContent value="document">
                <Card>
                  <CardContent className="p-4">
                    <Button variant="outline" className="mb-4">
                      <Download className="w-4 h-4 mr-2" /> Télécharger le contrat
                    </Button>
                    <pre className="text-xs bg-muted p-4 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap">
                      {selectedContrat.contrat?.document_texte || 'Document non généré'}
                    </pre>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default ContratsETAPage;
